import { useEffect, useRef, useState } from "react";
import { API } from "@/lib/api";
import { track } from "@/lib/analytics";
import { Mic, Square, Loader2, Volume2 } from "lucide-react";
import { toast } from "sonner";

const RECORD_MIME_CANDIDATES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
function pickMime() {
  if (typeof MediaRecorder === "undefined") return "";
  for (const m of RECORD_MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return "";
}

/**
 * VoiceButton — hold-to-talk / tap-to-toggle mic.
 * Records audio, sends multipart to /api/voice/turn, plays the audio reply,
 * and calls onExchange({user_text, assistant_text, session_id}) so parent can update chat log.
 */
export default function VoiceButton({ sessionId, onExchange, disabled }) {
  const [state, setState] = useState("idle"); // idle | recording | processing | speaking
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, []);

  const cleanup = () => {
    try {
      streamRef.current?.getTracks().forEach((t) => t.stop());
    } catch (_e) {}
    streamRef.current = null;
    mediaRef.current = null;
    chunksRef.current = [];
  };

  const start = async () => {
    if (disabled) return;
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      toast.error("This browser doesn't support voice recording.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMime();
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mediaRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = onStopHandler;
      rec.start();
      setState("recording");
    } catch (_e) {
      toast.error("Microphone permission is required.");
    }
  };

  const stop = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") {
      mediaRef.current.stop();
    }
  };

  const onStopHandler = async () => {
    setState("processing");
    const mime = mediaRef.current?.mimeType || "audio/webm";
    const blob = new Blob(chunksRef.current, { type: mime });
    cleanup();

    if (blob.size < 800) {
      toast.info("Didn't catch that — try holding a bit longer.");
      setState("idle");
      return;
    }

    try {
      const token = localStorage.getItem("harmonix_token") || "";
      const profileId = localStorage.getItem("harmonix_profile_id") || "";
      const ext = mime.includes("mp4") ? "mp4" : "webm";
      const fd = new FormData();
      fd.append("audio", blob, `clip.${ext}`);
      if (sessionId) fd.append("session_id", sessionId);

      const res = await fetch(`${API}/voice/turn`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          ...(profileId ? { "X-Profile-Id": profileId } : {}),
        },
        credentials: "include",
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Voice turn failed");
      }
      const data = await res.json();
      track("Voice Chat");
      onExchange && onExchange(data);

      // play audio reply
      setState("speaking");
      const audio = new Audio(`data:${data.audio_mime};base64,${data.audio_base64}`);
      audioRef.current = audio;
      audio.onended = () => setState("idle");
      audio.onerror = () => setState("idle");
      try {
        await audio.play();
      } catch (_e) {
        setState("idle");
      }
    } catch (e) {
      toast.error(e.message || "Voice failed. Please try again.");
      setState("idle");
    }
  };

  const stopSpeaking = () => {
    try {
      audioRef.current?.pause();
      audioRef.current = null;
    } catch (_e) {}
    setState("idle");
  };

  const label =
    state === "recording"
      ? "Stop recording"
      : state === "processing"
      ? "Processing…"
      : state === "speaking"
      ? "Stop speaking"
      : "Talk to Harmonix";

  const onClick = () => {
    if (state === "idle") start();
    else if (state === "recording") stop();
    else if (state === "speaking") stopSpeaking();
  };

  const bg =
    state === "recording"
      ? "#DC2626"
      : state === "processing"
      ? "#5C6B66"
      : state === "speaking"
      ? "#D96B43"
      : "#2D5A4C";

  const Icon =
    state === "recording"
      ? Square
      : state === "processing"
      ? Loader2
      : state === "speaking"
      ? Volume2
      : Mic;

  return (
    <button
      data-testid="voice-chat-btn"
      onClick={onClick}
      disabled={disabled || state === "processing"}
      aria-label={label}
      title={label}
      className="rounded-full h-12 w-12 grid place-items-center shrink-0 shadow-md transition-transform hover:-translate-y-0.5 disabled:opacity-60"
      style={{ background: bg, color: "white" }}
    >
      <Icon size={18} className={state === "processing" ? "animate-spin" : state === "recording" ? "pulse-dot" : ""} />
    </button>
  );
}
